import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import {
	containerButtonsCarouselContenSecondarytSX,
	containerDpCarouselContenMainSX,
	containerInfoDpCarouselContentMainSX,
} from '@/components/blocks/DpLibrary/DpCarouselContent/styles/container';
import {
	descriptionDpCarouselContentMainSX,
	titleDpCarouselContentMainSX,
} from '@/components/blocks/DpLibrary/DpCarouselContent/styles/typography';
import { Img } from '@/components/blocks/MaterialImage';
import { Grid, Typography } from '@mui/material';
import { Linkable } from '@/components/blocks/Linkable';
import { SliderContext, SliderItemContext } from '@/data/context/content';
import { useContext, useEffect } from 'react';
import { EventsContext } from '@/data/context/events';
import { useSettings } from '@/data/Settings';

type DpCarouselContentType = 'main' | 'secondary';

const getValues = (
	value: string | { mobile?: string; desktop?: string } | undefined,
	isMobile: boolean
) => {
	if (typeof value === 'string') return value;
	if (typeof value === 'object' && value.mobile && value.desktop) {
		return isMobile ? value.mobile : value.desktop;
	}
	return undefined;
};

const getButtons = (
	buttons:
		| {
				mobile?: { name: string; href: string }[];
				desktop?: { name: string; href: string }[];
		  }
		| undefined,
	isMobile: boolean
) => {
	if (typeof buttons === 'object' && buttons.mobile && buttons.desktop) {
		return isMobile ? buttons.mobile : buttons.desktop;
	}

	return [];
};

interface DpCarouselContentProps {
	title?: { mobile?: string; desktop?: string } | string;
	description?: { mobile?: string; desktop?: string } | string;
	image?: { mobile?: string; desktop?: string };
	buttons?: {
		mobile?: { name: string; href: string }[];
		desktop?: { name: string; href: string }[];
	};
	type?: DpCarouselContentType;
	href?: string;
	alt?: string;
	loading?: 'lazy' | 'eager';
	priority?: boolean;
}

interface DpCarouselContentItemProps {
	title: string;
	description: string;
	buttons: { name: string; href: string }[];
	src: string;
	alt?: string;
	loading?: 'lazy' | 'eager';
	priority?: boolean;
	mobile?: boolean;
}

const DpCarouselContentItem = ({
	src,
	alt,
	title,
	description,
	loading,
	priority,
	buttons,
	mobile = false,
}: DpCarouselContentItemProps) => (
	<Grid container sx={containerDpCarouselContenMainSX(mobile)}>
		<Grid item xs>
			<Img
				alt={alt}
				src={src}
				sx={{
					width: '100%',
					overflow: 'hidden',
				}}
				draggable="false"
				loading={loading}
				priority={priority}
			/>
		</Grid>
		<Grid item sx={containerInfoDpCarouselContentMainSX}>
			<Typography variant="h2" sx={titleDpCarouselContentMainSX}>
				{title}
			</Typography>
			<Typography sx={{ ...descriptionDpCarouselContentMainSX, mb: { sm: '48px' } }}>
				{description}
			</Typography>
			<Grid container columnSpacing={'18.5px'} sx={containerButtonsCarouselContenSecondarytSX}>
				{buttons?.map((item, index: number) => (
					<Grid item key={`id_button_${index}`}>
						<DpButton text={item.name} href={item.href} />
					</Grid>
				))}
			</Grid>
		</Grid>
	</Grid>
);

export const DpCarouselContent = ({
	title,
	description,
	image,
	buttons,
	href,
	alt,
	loading,
	priority,
}: DpCarouselContentProps) => {
	const { mouseMoved, activeSlide } = useContext(SliderContext);
	const { index } = useContext(SliderItemContext);
	const { onBannerClick, onBannerView } = useContext(EventsContext);
	const { settings } = useSettings();

	useEffect(() => {
		if (activeSlide === index) {
			onBannerView({
				gtm: {
					banner_name: alt ?? '',
					banner_url: href ?? '',
					banner_position: index ? index + 1 : 1,
					settings,
				},
			});
		}
	}, [activeSlide, alt, href, index, onBannerView, settings]);

	return (
		<Linkable
			href={href}
			draggable="false"
			onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
				if (mouseMoved) {
					e.preventDefault();
					return;
				}

				onBannerClick({
					gtm: {
						banner_name: alt ?? '',
						banner_url: href ?? '',
						banner_position: index ? index + 1 : 1,
						settings,
					},
				});
			}}
		>
			<DpCarouselContentItem
				src={getValues(image, false) ?? ''}
				alt={alt}
				title={getValues(title, false) ?? ''}
				description={getValues(description, false) ?? ''}
				buttons={getButtons(buttons, false)}
				loading={loading}
				priority={priority}
			/>
			<DpCarouselContentItem
				src={getValues(image, true) ?? ''}
				alt={alt}
				title={getValues(title, true) ?? ''}
				description={getValues(description, true) ?? ''}
				buttons={getButtons(buttons, true)}
				loading={loading}
				priority={priority}
				mobile
			/>
		</Linkable>
	);
};
